import { clientFor, type PageQuery, type PageResult } from '@yc/api-client'

const api = clientFor('/wms/api')

export type StocktakeStatus = 'draft' | 'counting' | 'confirming' | 'done' | 'cancelled'

/** 盘点范围：全仓 / 按库区 / 指定单个商品 */
export type StocktakeScope = 'all' | 'zone' | 'sku'

export interface Take {
  id: number
  tenant_id: number
  doc_no: string
  warehouse_id: number
  scope: StocktakeScope
  zone: string
  sku_id: number
  status: StocktakeStatus
  remark: string
  created_by: string
  created_at: string
  updated_at: string
}

/** 列表行：盘点单 + 仓库名 + 盘点项数 / 已录数 / 有差异数 */
export interface ListRow extends Take {
  warehouse_name: string
  item_count: number
  counted_count: number
  diff_count: number
}

/** 盘点项：建单时按范围拍下的账面快照，counted_qty 为 null 表示这行还没盘 */
export interface Item {
  id: number
  tenant_id: number
  take_id: number
  location_id: number
  sku_id: number
  batch_no: string
  book_qty: number
  counted_qty: number | null
  created_at: string
  updated_at: string
}

/** 盘点项 + 商品与库位展示信息 */
export interface ItemDetail extends Item {
  sku_code: string
  sku_name: string
  unit: string
  location_code: string
}

export interface Detail {
  take: Take
  warehouse_name: string
  items: ItemDetail[]
}

export interface CreateInput {
  warehouse_id: number
  scope: StocktakeScope
  zone?: string
  sku_id?: number
  remark?: string
}

export interface CountItem {
  id: number
  counted_qty: number
}

export interface StocktakeQuery extends PageQuery {
  keyword?: string
  warehouse?: number
  status?: string
}

/** 与后端 services/wms/internal/stocktake/model.go 的 StatusNames 同源两份，改一边必须改另一边 */
export const STATUS_LABELS: Record<StocktakeStatus, string> = {
  draft: '草稿',
  counting: '盘点中',
  confirming: '待确认',
  done: '已完成',
  cancelled: '已取消',
}

/** 与后端 services/wms/internal/stocktake/model.go 的 ScopeNames 同源两份 */
export const SCOPE_LABELS: Record<StocktakeScope, string> = {
  all: '全仓',
  zone: '按库区',
  sku: '指定商品',
}

/** 状态 → 表格里的标签色 */
export const STATUS_TAG: Record<StocktakeStatus, 'info' | 'warning' | 'primary' | 'success' | 'danger'> = {
  draft: 'info',
  counting: 'primary',
  confirming: 'warning',
  done: 'success',
  cancelled: 'danger',
}

/** 实盘 - 账面；没录实盘返回 null */
export function diffOf(item: Item): number | null {
  return item.counted_qty === null ? null : item.counted_qty - item.book_qty
}

export const listStocktakes = (q: StocktakeQuery): Promise<PageResult<ListRow>> =>
  api.get<PageResult<ListRow>>('/stocktakes', { ...q })

export const getStocktake = (id: number): Promise<Detail> => api.get<Detail>(`/stocktakes/${id}`)

export const createStocktake = (input: CreateInput): Promise<Take> =>
  api.post<Take>('/stocktakes', input)

export const deleteStocktake = (id: number): Promise<void> =>
  api.del<void>(`/stocktakes/${id}`)

export const startStocktake = (id: number): Promise<void> =>
  api.post<void>(`/stocktakes/${id}/start`, {})

/** 逐行录入实盘数 */
export const putStocktakeItem = (takeId: number, itemId: number, countedQty: number): Promise<void> =>
  api.put<void>(`/stocktakes/${takeId}/items/${itemId}`, { counted_qty: countedQty })

/** 一次性提交全部实盘数并转待确认 */
export const countStocktake = (id: number, items: CountItem[]): Promise<void> =>
  api.post<void>(`/stocktakes/${id}/count`, { items })

/** 按差异调账并归档 */
export const confirmStocktake = (id: number): Promise<Take> =>
  api.post<Take>(`/stocktakes/${id}/confirm`, {})

export const cancelStocktake = (id: number): Promise<void> =>
  api.post<void>(`/stocktakes/${id}/cancel`, {})
